import { atr, bollinger, donchian, ema, macd, rsi, sma, stochastic, supertrend } from "./indicators";
import {
  calculateAccumulationDistribution, calculateADX, calculateAwesomeOscillator,
  calculateCCI, calculateChaikinVolatility, calculateCMF, calculateDEMA, calculateDonchianChannels,
  calculateHMA, calculateIchimoku, calculateKeltner, calculateKaufmanEfficiencyRatio, calculateMACD,
  calculateMFI, calculateMomentum, calculateOBV, calculateParabolicSAR, calculateROC, calculateRollingStdDev,
  calculateStochRSI, calculateTEMA, calculateTSI, calculateTRIX, calculateVWAP,
  calculateVolumeOscillator, calculateWMA, calculateWilliamsR, calculateDPO,
} from "../Chart/indicatorCalculations";
import { GO_INDICATORS } from "../Chart/goIndicators";
import { detectHMMRegimesCached } from "../Chart/hmmRegimes";

/** No-code "rule strategies": Long when <A> <op> <B> (AND ...), Short when ..., Exit when ...
 *  A and B are price, a built-in indicator, one of your coded indicators, or a fixed number. */

const col = (candles, key) => candles.map((c) => c[key]);

function buildGoSources() {
  const candles = Array.from({ length: 240 }, (_, i) => {
    const close = 100 + i * 0.03 + Math.sin(i / 6) * 2;
    return { time: i * 60, open: close - 0.1, high: close + 0.8, low: close - 0.8, close, volume: 100 + (i % 31) * 7 };
  });
  const category = { trend: "Trend", momentum: "Momentum", volatility: "Volatility", volume: "Volume", patterns: "Patterns" };
  return Object.entries(GO_INDICATORS).flatMap(([key, def]) => {
    const params = Object.fromEntries((def.paramDefs || []).map((p) => [p.key, p.default]));
    const group = category[def.group] || "Advanced";
    const paramSpecs = (def.paramDefs || []).map((p) => ({ key: p.key, label: p.label, value: p.default, min: p.min, step: p.step }));
    const markerSource = { id: `go:${key}:markers`, group, label: `${def.fullLabel} · signal`, params: paramSpecs };
    try {
      const output = def.compute(candles, params) || {};
      const lines = output.lines || [];
      const lineSources = lines.map((line, index) => ({
        id: `go:${key}:${index}`,
        group,
        label: `${def.fullLabel}${lines.length > 1 ? ` · ${line.name || `Line ${index + 1}`}` : ""}`,
        params: paramSpecs,
      }));
      const hasMarkerOutput = Array.isArray(output.markers) || def.group === "patterns";
      return [...lineSources, ...(hasMarkerOutput ? [markerSource] : [])];
    } catch {
      // Keep every chart indicator selectable even if its sample calculation needs
      // more history or has no signal in the catalog's sample candles.
      return [def.group === "patterns" ? markerSource : {
        id: `go:${key}:0`, group, label: def.fullLabel, params: paramSpecs,
      }];
    }
  });
}

/** Every source the rule builder offers. `params` are the number boxes shown next to it. */
export const SOURCES = [
  { id: "close", group: "Price", label: "Close" },
  { id: "open", group: "Price", label: "Open" },
  { id: "high", group: "Price", label: "High" },
  { id: "low", group: "Price", label: "Low" },
  { id: "chart:ma", group: "Trend", label: "Moving Average", params: [{ key: "period", label: "Period", value: 20 }] },
  { id: "sma", group: "Indicators", label: "SMA", params: [{ key: "period", label: "Period", value: 20 }] },
  { id: "ema", group: "Indicators", label: "EMA", params: [{ key: "period", label: "Period", value: 20 }] },
  { id: "rsi", group: "Indicators", label: "RSI", params: [{ key: "period", label: "Period", value: 14 }] },
  { id: "atr", group: "Indicators", label: "ATR", params: [{ key: "period", label: "Period", value: 14 }] },
  { id: "bb_upper", group: "Indicators", label: "Bollinger upper", params: [{ key: "period", label: "Period", value: 20 }, { key: "mult", label: "Std", value: 2, step: 0.1 }] },
  { id: "bb_mid", group: "Indicators", label: "Bollinger middle", params: [{ key: "period", label: "Period", value: 20 }] },
  { id: "bb_lower", group: "Indicators", label: "Bollinger lower", params: [{ key: "period", label: "Period", value: 20 }, { key: "mult", label: "Std", value: 2, step: 0.1 }] },
  { id: "macd", group: "Indicators", label: "MACD line", params: [{ key: "fast", label: "Fast", value: 12 }, { key: "slow", label: "Slow", value: 26 }] },
  { id: "macd_signal", group: "Indicators", label: "MACD signal", params: [{ key: "fast", label: "Fast", value: 12 }, { key: "slow", label: "Slow", value: 26 }, { key: "signal", label: "Signal", value: 9 }] },
  { id: "macd_hist", group: "Indicators", label: "MACD histogram", params: [{ key: "fast", label: "Fast", value: 12 }, { key: "slow", label: "Slow", value: 26 }, { key: "signal", label: "Signal", value: 9 }] },
  { id: "stoch_k", group: "Indicators", label: "Stochastic %K", params: [{ key: "k", label: "%K", value: 14 }] },
  { id: "stoch_d", group: "Indicators", label: "Stochastic %D", params: [{ key: "k", label: "%K", value: 14 }, { key: "d", label: "%D", value: 3 }] },
  { id: "donchian_upper", group: "Indicators", label: "Donchian high (prev. N bars)", params: [{ key: "period", label: "Bars", value: 20 }] },
  { id: "donchian_lower", group: "Indicators", label: "Donchian low (prev. N bars)", params: [{ key: "period", label: "Bars", value: 20 }] },
  { id: "supertrend", group: "Indicators", label: "Supertrend line", params: [{ key: "period", label: "ATR", value: 10 }, { key: "mult", label: "Mult", value: 3, step: 0.1 }] },
  { id: "supertrend_dir", group: "Indicators", label: "Supertrend direction (+1 / -1)", params: [{ key: "period", label: "ATR", value: 10 }, { key: "mult", label: "Mult", value: 3, step: 0.1 }] },
  ...[
    ["wma", "Trend", "WMA", [{ key: "period", label: "Period", value: 20 }]],
    ["hma", "Trend", "HMA", [{ key: "period", label: "Period", value: 20 }]],
    ["dema", "Trend", "DEMA", [{ key: "period", label: "Period", value: 20 }]],
    ["tema", "Trend", "TEMA", [{ key: "period", label: "Period", value: 20 }]],
    ["vwap", "Trend", "VWAP", []],
    ["sar", "Trend", "Parabolic SAR", [{ key: "step", label: "Step", value: 0.02, step: 0.01 }, { key: "maxStep", label: "Max", value: 0.2, step: 0.01 }]],
    ["ichimoku_conversion", "Trend", "Ichimoku conversion", []],
    ["ichimoku_base", "Trend", "Ichimoku base", []],
    ["ichimoku_span_a", "Trend", "Ichimoku span A", []],
    ["ichimoku_span_b", "Trend", "Ichimoku span B", []],
    ["ichimoku_chikou", "Trend", "Ichimoku lagging span", []],
    ["donchian_mid", "Trend", "Donchian middle", [{ key: "period", label: "Period", value: 20 }]],
    ["keltner_upper", "Trend", "Keltner upper", [{ key: "emaPeriod", label: "EMA", value: 20 }, { key: "atrPeriod", label: "ATR", value: 10 }, { key: "mult", label: "Mult", value: 2, step: 0.1 }]],
    ["keltner_mid", "Trend", "Keltner middle", [{ key: "emaPeriod", label: "EMA", value: 20 }, { key: "atrPeriod", label: "ATR", value: 10 }, { key: "mult", label: "Mult", value: 2, step: 0.1 }]],
    ["keltner_lower", "Trend", "Keltner lower", [{ key: "emaPeriod", label: "EMA", value: 20 }, { key: "atrPeriod", label: "ATR", value: 10 }, { key: "mult", label: "Mult", value: 2, step: 0.1 }]],
    ["cci", "Momentum", "CCI", [{ key: "period", label: "Period", value: 20 }]],
    ["williams", "Momentum", "Williams %R", [{ key: "period", label: "Period", value: 14 }]],
    ["roc", "Momentum", "Rate of change", [{ key: "period", label: "Period", value: 12 }]],
    ["momentum", "Momentum", "Momentum", [{ key: "period", label: "Period", value: 10 }]],
    ["stochrsi_k", "Momentum", "Stochastic RSI %K", []],
    ["stochrsi_d", "Momentum", "Stochastic RSI %D", []],
    ["macd_hist", "Momentum", "MACD histogram", [{ key: "fast", label: "Fast", value: 12 }, { key: "slow", label: "Slow", value: 26 }, { key: "signal", label: "Signal", value: 9 }]],
    ["ao", "Momentum", "Awesome oscillator", []],
    ["tsi", "Momentum", "True strength index", []],
    ["trix", "Momentum", "TRIX", [{ key: "period", label: "Period", value: 15 }]],
    ["dpo", "Momentum", "Detrended price oscillator", [{ key: "period", label: "Period", value: 20 }]],
    ["adx", "Momentum", "Average directional index", [{ key: "period", label: "Period", value: 14 }]],
    ["stddev", "Volatility", "Standard deviation", [{ key: "period", label: "Period", value: 20 }]],
    ["chaikin_vol", "Volatility", "Chaikin volatility", []],
    ["obv", "Volume", "On balance volume", []],
    ["cmf", "Volume", "Chaikin money flow", [{ key: "period", label: "Period", value: 20 }]],
    ["mfi", "Volume", "Money flow index", [{ key: "period", label: "Period", value: 14 }]],
    ["ad_line", "Volume", "Accumulation / distribution", []],
    ["volume_osc", "Volume", "Volume oscillator", []],
    ["kaufman", "Advanced", "Kaufman trend score", [
      { key: "period", label: "Period", value: 20, min: 2, step: 1 },
      { key: "smooth", label: "Smooth", value: 5, min: 1, step: 1 },
    ]],
  ].map(([id, group, label, params]) => ({ id: `chart:${id}`, group, label, params })),
  { id: "kaufman_regime_range", group: "Regimes · Kaufman", label: "Range state (1 / 0)", params: [
    { key: "period", label: "Period", value: 20, min: 2, step: 1 },
    { key: "smooth", label: "Smooth", value: 5, min: 1, step: 1 },
    { key: "rangeThreshold", label: "Range ≤", value: 0.25, min: 0, step: 0.01 },
  ] },
  { id: "kaufman_regime_trend", group: "Regimes · Kaufman", label: "Trend state (1 / 0)", params: [
    { key: "period", label: "Period", value: 20, min: 2, step: 1 },
    { key: "smooth", label: "Smooth", value: 5, min: 1, step: 1 },
    { key: "trendThreshold", label: "Trend ≥", value: 0.4, min: 0, step: 0.01 },
  ] },
  { id: "kaufman_regime_mixed", group: "Regimes · Kaufman", label: "Mixed state (1 / 0)", params: [
    { key: "period", label: "Period", value: 20, min: 2, step: 1 },
    { key: "smooth", label: "Smooth", value: 5, min: 1, step: 1 },
    { key: "rangeThreshold", label: "Range ≤", value: 0.25, min: 0, step: 0.01 },
    { key: "trendThreshold", label: "Trend ≥", value: 0.4, min: 0, step: 0.01 },
  ] },
  { id: "hmm_regime_range", group: "Regimes · HMM", label: "Range state · holdout (1 / 0)", params: [{ key: "lookback", label: "Lookback", value: 20, min: 2, step: 1 }] },
  { id: "hmm_regime_trend", group: "Regimes · HMM", label: "Trend state · holdout (1 / 0)", params: [{ key: "lookback", label: "Lookback", value: 20, min: 2, step: 1 }] },
  { id: "hmm_regime_high_volatility", group: "Regimes · HMM", label: "High volatility state · holdout (1 / 0)", params: [{ key: "lookback", label: "Lookback", value: 20, min: 2, step: 1 }] },
  ...buildGoSources(),
];

export const OPERATORS = [
  { id: "xa", label: "crosses above" },
  { id: "xb", label: "crosses below" },
  { id: "gt", label: "is above" },
  { id: "lt", label: "is below" },
];

const SOURCE_BY_ID = Object.fromEntries(SOURCES.map((s) => [s.id, s]));

export function sourceDefaults(id) {
  const def = SOURCE_BY_ID[id];
  const out = {};
  (def?.params || []).forEach((p) => {
    out[p.key] = p.value;
  });
  return out;
}

const posInt = (v, fallback) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n >= 1 ? Math.min(n, 2000) : fallback;
};
const posNum = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};
const boundedThreshold = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(1, Math.max(0, number)) : fallback;
};

/** Values for one built-in source. Pure. */
export function builtinSeries(candles, ref) {
  const p = { ...sourceDefaults(ref.src), ...(ref.p || {}) };
  const c = col(candles, "close");
  const h = col(candles, "high");
  const l = col(candles, "low");
  const seriesValues = (points) => {
    const byTime = new Map((points || []).map((point) => [Number(point.time), point.value]));
    return candles.map((candle) => byTime.has(Number(candle.time)) ? byTime.get(Number(candle.time)) : NaN);
  };
  const kaufmanRegime = /^kaufman_regime_(range|trend|mixed)$/.exec(ref.src);
  if (kaufmanRegime) {
    const kind = kaufmanRegime[1];
    const values = new Map(calculateKaufmanEfficiencyRatio(candles, posInt(p.period, 20), posInt(p.smooth, 5)).map((point) => [Number(point.time), point.value]));
    const rangeThreshold = boundedThreshold(p.rangeThreshold, 0.25);
    const trendThreshold = Math.max(rangeThreshold, boundedThreshold(p.trendThreshold, 0.4));
    return candles.map((candle) => {
      const value = values.get(Number(candle.time));
      if (!Number.isFinite(value)) return NaN;
      if (kind === "range") return Number(value <= rangeThreshold);
      if (kind === "trend") return Number(value >= trendThreshold);
      return Number(value > rangeThreshold && value < trendThreshold);
    });
  }
  const hmmRegime = /^hmm_regime_(range|trend|high_volatility)$/.exec(ref.src);
  if (hmmRegime) {
    const kind = hmmRegime[1] === "high_volatility" ? "highVol" : hmmRegime[1];
    const model = detectHMMRegimesCached(candles, { lookback: p.lookback });
    if (model.status !== "ready") {
      throw new Error(`HMM regime filter unavailable: ${model.reason || "the model could not be fitted to these candles."}`);
    }
    // Only forward-filtered holdout states can drive a backtest. Training labels
    // are retrospective and are shown on the main chart for analysis only.
    const holdout = new Map(model.bars.filter((bar) => bar.phase === "holdout").map((bar) => [Number(bar.time), bar.kind]));
    return candles.map((candle) => {
      const time = Number(candle.time);
      return holdout.has(time) ? Number(holdout.get(time) === kind) : NaN;
    });
  }
  if (ref.src.startsWith("go:")) {
    const [, key, lineIndex] = ref.src.split(":");
    const definition = GO_INDICATORS[key];
    if (!definition) throw new Error(`Unknown chart indicator "${key}"`);
    const settings = Object.fromEntries((definition.paramDefs || []).map((param) => [param.key, p[param.key] ?? param.default]));
    const output = definition.compute(candles, settings) || {};
    if (lineIndex === "markers") {
      const byTime = new Map((output.markers || []).map((marker) => [marker.time, marker.position === "belowBar" ? 1 : -1]));
      const values = candles.map(() => NaN);
      candles.forEach((candle, index) => {
        const markerIndex = key === "fractals" ? index - 2 : index;
        if (markerIndex >= 0 && byTime.has(candles[markerIndex].time)) values[index] = byTime.get(candles[markerIndex].time);
      });
      return values;
    }
    const line = output.lines?.[Number(lineIndex)];
    if (!line?.values) throw new Error(`No backtest line is available for ${definition.fullLabel}`);
    return line.values.map((value) => Number.isFinite(value) ? value : NaN);
  }
  if (ref.src.startsWith("chart:")) {
    const [, id] = ref.src.split(":");
    const period = Number(p.period) || 14;
    switch (id) {
      case "ma": return sma(c, posInt(p.period, 20));
      case "wma": return seriesValues(calculateWMA(candles, period));
      case "hma": return seriesValues(calculateHMA(candles, period));
      case "dema": return seriesValues(calculateDEMA(candles, period));
      case "tema": return seriesValues(calculateTEMA(candles, period));
      case "vwap": return seriesValues(calculateVWAP(candles));
      case "sar": return seriesValues(calculateParabolicSAR(candles, Number(p.step) || 0.02, Number(p.maxStep) || 0.2));
      case "ichimoku_conversion": return seriesValues(calculateIchimoku(candles).tenkan);
      case "ichimoku_base": return seriesValues(calculateIchimoku(candles).kijun);
      case "ichimoku_span_a": return seriesValues(calculateIchimoku(candles).spanA);
      case "ichimoku_span_b": return seriesValues(calculateIchimoku(candles).spanB);
      case "ichimoku_chikou": return seriesValues(calculateIchimoku(candles).chikou);
      case "donchian_mid": return seriesValues(calculateDonchianChannels(candles, period).middle);
      case "keltner_upper": return seriesValues(calculateKeltner(candles, Number(p.emaPeriod) || 20, Number(p.atrPeriod) || 10, Number(p.mult) || 2).upper);
      case "keltner_mid": return seriesValues(calculateKeltner(candles, Number(p.emaPeriod) || 20, Number(p.atrPeriod) || 10, Number(p.mult) || 2).middle);
      case "keltner_lower": return seriesValues(calculateKeltner(candles, Number(p.emaPeriod) || 20, Number(p.atrPeriod) || 10, Number(p.mult) || 2).lower);
      case "cci": return seriesValues(calculateCCI(candles, period));
      case "williams": return seriesValues(calculateWilliamsR(candles, period));
      case "roc": return seriesValues(calculateROC(candles, period));
      case "momentum": return seriesValues(calculateMomentum(candles, period));
      case "stochrsi_k": return seriesValues(calculateStochRSI(candles, 14, 14, 3, 3).k);
      case "stochrsi_d": return seriesValues(calculateStochRSI(candles, 14, 14, 3, 3).d);
      case "macd_hist": return seriesValues(calculateMACD(candles, Number(p.fast) || 12, Number(p.slow) || 26, Number(p.signal) || 9).histogram);
      case "ao": return seriesValues(calculateAwesomeOscillator(candles));
      case "tsi": return seriesValues(calculateTSI(candles));
      case "trix": return seriesValues(calculateTRIX(candles, period));
      case "dpo": return seriesValues(calculateDPO(candles, period));
      case "adx": return seriesValues(calculateADX(candles, period).adx);
      case "stddev": return seriesValues(calculateRollingStdDev(candles, period));
      case "chaikin_vol": return seriesValues(calculateChaikinVolatility(candles));
      case "obv": return seriesValues(calculateOBV(candles));
      case "cmf": return seriesValues(calculateCMF(candles, period));
      case "mfi": return seriesValues(calculateMFI(candles, period));
      case "ad_line": return seriesValues(calculateAccumulationDistribution(candles));
      case "volume_osc": return seriesValues(calculateVolumeOscillator(candles));
      case "kaufman": return seriesValues(calculateKaufmanEfficiencyRatio(candles, posInt(p.period, 20), posInt(p.smooth, 5)));
      default: throw new Error(`Unknown chart indicator source "${id}"`);
    }
  }
  switch (ref.src) {
    case "close":
    case "open":
    case "high":
    case "low":
      return col(candles, ref.src);
    case "sma":
      return sma(c, posInt(p.period, 20));
    case "ema":
      return ema(c, posInt(p.period, 20));
    case "rsi":
      return rsi(c, posInt(p.period, 14));
    case "atr":
      return atr(h, l, c, posInt(p.period, 14));
    case "bb_upper":
      return bollinger(c, posInt(p.period, 20), posNum(p.mult, 2)).upper;
    case "bb_mid":
      return bollinger(c, posInt(p.period, 20), 2).mid;
    case "bb_lower":
      return bollinger(c, posInt(p.period, 20), posNum(p.mult, 2)).lower;
    case "macd":
      return macd(c, posInt(p.fast, 12), posInt(p.slow, 26), 9).line;
    case "macd_signal":
      return macd(c, posInt(p.fast, 12), posInt(p.slow, 26), posInt(p.signal, 9)).signal;
    case "macd_hist":
      return macd(c, posInt(p.fast, 12), posInt(p.slow, 26), posInt(p.signal, 9)).hist;
    case "stoch_k":
      return stochastic(h, l, c, posInt(p.k, 14), 3).k;
    case "stoch_d":
      return stochastic(h, l, c, posInt(p.k, 14), posInt(p.d, 3)).d;
    case "donchian_upper":
      return donchian(h, l, posInt(p.period, 20)).upper;
    case "donchian_lower":
      return donchian(h, l, posInt(p.period, 20)).lower;
    case "supertrend":
      return supertrend(h, l, c, posInt(p.period, 10), posNum(p.mult, 3)).line;
    case "supertrend_dir":
      return supertrend(h, l, c, posInt(p.period, 10), posNum(p.mult, 3)).dir;
    default:
      throw new Error(`Unknown source "${ref.src}"`);
  }
}

/** Coded indicators a rule uses: [{itemId, output}] (each listed once). Pure. */
export function customRefs(rule) {
  const out = [];
  const seen = new Set();
  ["long", "short", "exit"].forEach((side) => {
    (rule?.[side] || []).forEach((cond) => {
      [cond.a, cond.b].forEach((ref) => {
        if (ref?.src === "custom" && ref.itemId) {
          const key = `${ref.itemId}|${ref.output || ""}`;
          if (!seen.has(key)) {
            seen.add(key);
            out.push({ itemId: ref.itemId, output: ref.output || "" });
          }
        }
      });
    });
  });
  return out;
}

/** Values for any operand. `custom` = {"itemId|output": number[]} already computed by Python. Pure. */
export function operandSeries(candles, ref, custom = {}) {
  const n = candles.length;
  if (!ref || ref.src === "number") {
    const v = Number(ref?.value);
    return new Array(n).fill(Number.isFinite(v) ? v : NaN);
  }
  if (ref.src === "custom") {
    const values = custom[`${ref.itemId}|${ref.output || ""}`];
    if (!values) throw new Error("A coded indicator used by this rule is missing (deleted?). Edit the rule.");
    return values.map((v) => (v === null || v === undefined ? NaN : Number(v)));
  }
  return builtinSeries(candles, ref);
}

const ok = (...v) => v.every((x) => Number.isFinite(x));
const isRegimeStateSource = (ref) => /^(hmm|kaufman)_regime_(range|trend|mixed|high_volatility)$/.test(String(ref?.src || ""));

/** true/false per bar for one condition. Pure. */
export function conditionSeries(candles, cond, custom = {}) {
  const a = operandSeries(candles, cond.a, custom);
  if (isRegimeStateSource(cond.a)) return a.map((value) => Number.isFinite(value) && value > 0.5);
  const b = operandSeries(candles, cond.b, custom);
  return a.map((_, i) => {
    if (!ok(a[i], b[i])) return false;
    switch (cond.op) {
      case "gt":
        return a[i] > b[i];
      case "lt":
        return a[i] < b[i];
      case "xa":
        return i > 0 && ok(a[i - 1], b[i - 1]) && a[i - 1] <= b[i - 1] && a[i] > b[i];
      case "xb":
        return i > 0 && ok(a[i - 1], b[i - 1]) && a[i - 1] >= b[i - 1] && a[i] < b[i];
      default:
        return false;
    }
  });
}

function allOf(candles, conds, custom, logic = "and") {
  if (!conds || !conds.length) return null;
  const parts = conds.map((c) => conditionSeries(candles, c, custom));
  return parts[0].map((_, i) => (logic === "or" ? parts.some((part) => part[i]) : parts.every((part) => part[i])));
}

/** Desired position per bar (1 / -1 / 0) for a rule strategy. Pure.
 *  Long signal -> long, short signal -> short (both at once = keep), exit signal -> flat, else keep. */
export function rulePositions(candles, rule, custom = {}) {
  const n = candles.length;
  const longSig = allOf(candles, rule.long, custom, rule.logic?.long);
  const shortSig = allOf(candles, rule.short, custom, rule.logic?.short);
  const exitSig = allOf(candles, rule.exit, custom, rule.logic?.exit);
  const out = new Array(n).fill(0);
  let pos = 0;
  for (let i = 0; i < n; i += 1) {
    const L = longSig ? longSig[i] : false;
    const S = shortSig ? shortSig[i] : false;
    if (L && !S) pos = 1;
    else if (S && !L) pos = -1;
    else if (!L && !S && exitSig && exitSig[i]) pos = 0;
    out[i] = pos;
  }
  return out;
}

/** A starting rule for "New rule strategy". */
export function blankRule() {
  return {
    name: "",
    long: [{ a: { src: "rsi", p: { period: 14 } }, op: "xa", b: { src: "number", value: 30 } }],
    short: [],
    exit: [{ a: { src: "rsi", p: { period: 14 } }, op: "xa", b: { src: "number", value: 60 } }],
    logic: { long: "and", short: "and", exit: "and" },
  };
}

function refText(ref, itemNames) {
  if (!ref) return "?";
  if (ref.src === "number") return String(ref.value);
  if (ref.src === "custom") {
    // itemNames: {id: name} or {id: {name, outputs}}; the line name is only shown when there are several
    const info = itemNames[ref.itemId];
    const name = (typeof info === "string" ? info : info?.name) || "missing indicator";
    const several = typeof info === "object" && info?.outputs?.length > 1;
    return several && ref.output ? `${name} · ${ref.output}` : name;
  }
  const def = SOURCE_BY_ID[ref.src];
  if (!def) return ref.src;
  const p = { ...sourceDefaults(ref.src), ...(ref.p || {}) };
  const vals = (def.params || []).map((x) => p[x.key]);
  if (isRegimeStateSource(ref)) {
    const family = def.group.split("·").pop().trim();
    const state = def.label.replace(" state (1 / 0)", "");
    return `${family} ${state}${vals.length ? ` (${vals.join(", ")})` : ""}`;
  }
  return vals.length ? `${def.label}(${vals.join(", ")})` : def.label;
}

/** Plain-English summary shown under the rule in the list. Pure. */
export function describeRule(rule, itemNames = {}) {
  const op = Object.fromEntries(OPERATORS.map((o) => [o.id, o.label]));
  const side = (key, conds) => conds.map((c) => isRegimeStateSource(c.a)
    ? `${refText(c.a, itemNames)} is active`
    : `${refText(c.a, itemNames)} ${op[c.op] || c.op} ${refText(c.b, itemNames)}`).join(rule.logic?.[key] === "or" ? " or " : " and ");
  const parts = [];
  if (rule.long?.length) parts.push(`Long when ${side("long", rule.long)}`);
  if (rule.short?.length) parts.push(`short when ${side("short", rule.short)}`);
  if (rule.exit?.length) parts.push(`exit when ${side("exit", rule.exit)}`);
  return parts.join("; ") + (parts.length ? "." : "No conditions yet.");
}
